import React, { useEffect, useState } from "react";
import { Loader2, Network, ListChecks, FileSpreadsheet, BookOpen } from "lucide-react";
import { getStandards, FILES } from "../../lib/standardsApi";
import DependencyMap from "./DependencyMap";
import Prerequisites from "./Prerequisites";
import TablePage from "./TablePage";
import { DownloadButton } from "./parts";
import { cn } from "../../lib/utils";

const Item = ({ on, onClick, testid, icon: Icon, children, aside }) => (
  <button data-testid={testid} onClick={onClick}
    className={cn("w-full text-left px-2.5 py-1.5 rounded-sm flex items-center gap-2 border-l-2 transition-colors text-[12.5px]", on ? "bg-primary/10 border-l-primary text-primary font-semibold" : "border-transparent hover:bg-surface-2")}>
    {Icon && <Icon size={12} className="shrink-0" />}<span className="flex-1 truncate">{children}</span>{aside}</button>
);

function Rail({ data, page, onPage }) {
  return (
    <div className="space-y-3">
      <div className="tile p-2 space-y-0.5" data-testid="std-rail-overview">
        <div className="px-2.5 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-muted">Overview</div>
        <Item testid="std-nav-map" icon={Network} on={page === "map"} onClick={() => onPage("map")}>Dependency map</Item>
        <Item testid="std-nav-prereq" icon={ListChecks} on={page === "prereq"} onClick={() => onPage("prereq")}>Prerequisites by module</Item>
        <Item testid="std-nav-general" icon={BookOpen} on={page === "general"} onClick={() => onPage("general")}>File format basics</Item>
      </div>
      <div className="tile p-2" data-testid="std-rail-tables">
        {data.groups.map(g => (
          <div key={g.id} className="pb-1">
            <div className="px-2.5 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.2em] font-bold text-muted">{g.label}</div>
            {data.tables.filter(t => t.group === g.id).map(t => (
              <Item key={t.id} testid={`std-nav-${t.id}`} icon={FileSpreadsheet} on={page === t.id} onClick={() => onPage(t.id)}
                aside={<span className={cn("w-1.5 h-1.5 rounded-full shrink-0", t.silver?.in_silver ? "bg-success" : "bg-critical/70")} title={t.silver?.in_silver ? "In Silver" : "Missing"} />}>
                <span className="font-mono-tabular text-[10px] text-muted mr-1.5">{String(t.load_step).padStart(2, "0")}</span>{t.label}</Item>))}
          </div>))}
      </div>
      <div className="tile p-3 space-y-2" data-testid="std-rail-downloads">
        <div className="kpi-label">Downloads for every table</div>
        <div className="flex flex-wrap gap-2">
          <DownloadButton file={FILES.dictionary()} label="Data dictionary" testid="std-dl-dictionary" />
          <DownloadButton primary file={FILES.all()} label="Download all (zip)" testid="std-dl-all" />
        </div>
        <div className="text-[11px] text-muted leading-snug">The zip holds every blank template and filled example, numbered in load order, plus the dictionary.</div>
      </div>
    </div>
  );
}

function General({ data }) {
  return (
    <div className="tile p-5 space-y-3 animate-fade-in" data-testid="std-general">
      <h2 className="font-display text-lg font-semibold tracking-tight">File format basics · standard {data.version}</h2>
      <ul className="space-y-1.5 text-[13px]">{data.general.map(g => <li key={g} className="flex gap-2"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />{g}</li>)}</ul>
      <div className="text-[12.5px] text-muted">Load order: {data.load_order.map((t, i) => `${i + 1}. ${data.tables.find(x => x.id === t).label}`).join(" · ")}</div>
    </div>
  );
}

// Semantic Data Explorer → Data Standards (IT Head and COO): guide to the 13 tables, dependency map, module prerequisites and templates.
export default function StandardsView({ initialTable, initialCol }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(initialTable || "prereq");
  const [col, setCol] = useState(initialCol || null);
  useEffect(() => { getStandards().then(setData).catch(e => setErr(e.message)); }, []);
  const open = (p) => { setCol(null); setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
  if (!data) return <div className="tile p-6 text-sm text-muted" data-testid="std-loading">{err || <><Loader2 size={13} className="inline animate-spin mr-2" />Loading the data standards…</>}</div>;
  return (
    <div className="grid grid-cols-12 gap-6" data-testid="std-view">
      <div className="col-span-12 lg:col-span-3"><Rail data={data} page={page} onPage={open} /></div>
      <div className="col-span-12 lg:col-span-9 min-w-0">
        {page === "map" && <DependencyMap data={data} onOpen={open} />}
        {page === "prereq" && <Prerequisites data={data} onOpen={open} />}
        {page === "general" && <General data={data} />}
        {data.tables.some(t => t.id === page) && <TablePage t={page} col={col} onOpen={open} />}
      </div>
    </div>
  );
}
